// localStorage persistence for holiday data

import { STORAGE_KEY, STORAGE_VERSION } from './constants';
import { HolidayStorage, Calendar, HolidayEvent } from './types';

/**
 * Load holiday data from localStorage
 */
export function loadHolidayData(): HolidayStorage | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored) as HolidayStorage;

    // Validate structure
    if (
      !data ||
      typeof data !== 'object' ||
      !Array.isArray(data.calendars) ||
      typeof data.events !== 'object'
    ) {
      console.warn('Invalid holiday data structure, clearing storage');
      clearHolidayData();
      return null;
    }

    // Handle version migrations if needed
    return migrateStorage(data);
  } catch (error) {
    console.error('Failed to load holiday data:', error);
    return null;
  }
}

/**
 * Save holiday data to localStorage
 */
export function saveHolidayData(data: HolidayStorage): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save holiday data:', error);

    if (error instanceof Error && error.name === 'Quota ExceededError') {
      throw new Error(
        'Storage quota exceeded. Please remove some calendars to free up space.',
      );
    }

    throw new Error('Failed to save holiday data');
  }
}

/**
 * Clear all holiday data from localStorage
 */
export function clearHolidayData(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear holiday data:', error);
  }
}

/**
 * Migrate storage to current version
 */
function migrateStorage(data: any): HolidayStorage {
  // Currently on v1, no migrations needed yet
  if (!data.version || data.version < STORAGE_VERSION) {
    console.info('Migrating holiday storage to v' + STORAGE_VERSION);

    return {
      version: STORAGE_VERSION,
      calendars: data.calendars || [],
      events: data.events || {},
    };
  }

  return data;
}

/**
 * Add calendar to storage
 */
export function addCalendarToStorage(calendar: Calendar): void {
  const data = loadHolidayData() || {
    version: STORAGE_VERSION,
    calendars: [],
    events: {},
  };

  // Check if calendar already exists
  const existingIndex = data.calendars.findIndex((c) => c.id === calendar.id);

  if (existingIndex >= 0) {
    // Update existing calendar
    data.calendars[existingIndex] = calendar;
  } else {
    // Add new calendar
    data.calendars.push(calendar);
  }

  saveHolidayData(data);
}

/**
 * Remove calendar from storage (also removes associated events)
 */
export function removeCalendarFromStorage(calendarId: string): void {
  const data = loadHolidayData();
  if (!data) return;

  // Remove calendar
  data.calendars = data.calendars.filter((c) => c.id !== calendarId);

  // Remove events for this calendar
  Object.keys(data.events).forEach((date) => {
    data.events[date] = data.events[date].filter(
      (e) => e.calendarId !== calendarId,
    );

    // Remove date entry if no events left
    if (data.events[date].length === 0) {
      delete data.events[date];
    }
  });

  saveHolidayData(data);
}

/**
 * Toggle calendar enabled state
 */
export function toggleCalendarInStorage(
  calendarId: string,
  enabled: boolean,
): void {
  const data = loadHolidayData();
  if (!data) return;

  const calendar = data.calendars.find((c) => c.id === calendarId);
  if (calendar) {
    calendar.enabled = enabled;
    saveHolidayData(data);
  }
}

/**
 * Rename calendar
 */
export function renameCalendarInStorage(
  calendarId: string,
  newName: string,
): void {
  const data = loadHolidayData();
  if (!data) return;

  const calendar = data.calendars.find((c) => c.id === calendarId);
  if (calendar) {
    calendar.name = newName;
    calendar.lastModified = new Date().toISOString();
    saveHolidayData(data);
  }
}

/**
 * Add events to storage
 */
export function addEventsToStorage(events: HolidayEvent[]): void {
  const data = loadHolidayData() || {
    version: STORAGE_VERSION,
    calendars: [],
    events: {},
  };

  // Group events by date and add them
  events.forEach((event) => {
    if (!data.events[event.date]) {
      data.events[event.date] = [];
    }

    // Avoid duplicates (same calendar + date + title)
    const isDuplicate = data.events[event.date].some(
      (e) =>
        e.calendarId === event.calendarId &&
        e.date === event.date &&
        e.title === event.title,
    );

    if (!isDuplicate) {
      data.events[event.date].push(event);
    }
  });

  saveHolidayData(data);
}

/**
 * Get events for a specific date
 */
export function getEventsForDate(date: string): HolidayEvent[] {
  const data = loadHolidayData();
  if (!data) return [];

  return data.events[date] || [];
}

/**
 * Get all enabled calendars
 */
export function getEnabledCalendars(): Calendar[] {
  const data = loadHolidayData();
  if (!data) return [];

  return data.calendars.filter((c) => c.enabled);
}
